import { useCallback, useMemo, useRef, useState } from "react";

export type Phase = "ready" | "work" | "rest" | "complete";

export type EngineConfig = {
    workSec: number;
    restSec: number;
    rounds: number;
    whistleStart: boolean;
};

type EngineState = {
    phase: Phase;
    roundIndex: number; // 0-based
    isRunning: boolean;
    phaseEndsAtMs: number | null;
};

export function useTrainingEngine(cfg: EngineConfig) {
    const [state, setState] = useState<EngineState>({
        phase: "ready",
        roundIndex: 0,
        isRunning: false,
        phaseEndsAtMs: null,
    });

    const cfgRef = useRef(cfg);
    cfgRef.current = cfg;

    const start = useCallback((nowMs: number) => {
        const workMs = cfgRef.current.workSec * 1000;
        setState({
            phase: "work",
            roundIndex: 0,
            isRunning: true,
            phaseEndsAtMs: nowMs + workMs,
        });
    }, []);

    const pause = useCallback(() => {
        setState(s => ({ ...s, isRunning: false }));
    }, []);

    const resume = useCallback((nowMs: number, remainingMs: number) => {
        setState(s => ({ ...s, isRunning: true, phaseEndsAtMs: nowMs + remainingMs }));
    }, []);

    const next = useCallback((nowMs: number) => {
        setState(s => {
            const { workSec, restSec, rounds } = cfgRef.current;
            const workMs = workSec * 1000;
            const restMs = restSec * 1000;

            if (s.phase === "complete") return s;

            // If currently work → rest (same round)
            if (s.phase === "work") {
                if (s.roundIndex + 1 >= rounds) {
                    return { ...s, phase: "complete", isRunning: false, phaseEndsAtMs: null };
                }
                return { ...s, phase: "rest", phaseEndsAtMs: nowMs + restMs };
            }

            // rest → next round work OR complete
            if (s.phase === "rest") {
                const nextRound = s.roundIndex + 1;
                if (nextRound >= rounds) {
                    return { ...s, phase: "complete", isRunning: false, phaseEndsAtMs: null };
                }
                return { ...s, roundIndex: nextRound, phase: "work", phaseEndsAtMs: nowMs + workMs };
            }

            // ready → work
            if (s.phase === "ready") {
                return { ...s, phase: "work", isRunning: true, phaseEndsAtMs: nowMs + workMs };
            }

            return s;
        });
    }, []);

    const reset = useCallback(() => {
        setState({ phase: "ready", roundIndex: 0, isRunning: false, phaseEndsAtMs: null });
    }, []);

    return useMemo(() => ({ state, start, pause, resume, next, reset, setState }), [state, start, pause, resume, next, reset]);
}
